import { ENV, fromRuntime, readFlag } from './env';

// A `VITE_FEATURE_<KEY>` line from .env.<environment>: read directly under the
// Vite dev server, injected as `FEATURE_<KEY>` at container start in a built
// image (docker-entrypoint.sh reads the same file, picked by APP_ENV).
// Vite only inlines static `import.meta.env.VITE_*` accesses, so the dev value
// is passed in rather than looked up by name.
const flag = (key, devValue, fallback) =>
  readFlag(fromRuntime(`FEATURE_${key}`, devValue), fallback);

/**
 * Per-environment UI switches. To add one: a key here plus a
 * `VITE_FEATURE_<KEY>` line in each `.env.<environment>` file.
 */
export const FEATURES = Object.freeze({
  // TIMED_HOLD set-ceiling countdown chip over the video.
  SET_CEILING_COUNTDOWN: flag(
    'SET_CEILING_COUNTDOWN',
    import.meta.env.VITE_FEATURE_SET_CEILING_COUNTDOWN
  ),
  // Realtime voice-agent diagnostics panel (Ctrl+Alt+R).
  AI_DEBUG_PANEL: flag('AI_DEBUG_PANEL', import.meta.env.VITE_FEATURE_AI_DEBUG_PANEL),
  // Exercise diagnostic overlays: hold telemetry, aspect ratio.
  DEBUG_OVERLAYS: flag('DEBUG_OVERLAYS', import.meta.env.VITE_FEATURE_DEBUG_OVERLAYS),
  // Sends an exercise session that has been hidden too long back to the camera
  // screen. Off outside development until it has been measured on real sessions.
  HIDE_EXITS_SESSION: flag('HIDE_EXITS_SESSION', import.meta.env.VITE_FEATURE_HIDE_EXITS_SESSION),
  // Audio inspector inside AudiosSection.
  AUDIO_DEBUG_CONTROLS: flag(
    'AUDIO_DEBUG_CONTROLS',
    import.meta.env.VITE_FEATURE_AUDIO_DEBUG_CONTROLS
  ),
  // Network diagnostics panel (Ctrl+Alt+N) and the recorders that feed it.
  // Off means every record() call returns before doing any work.
  NETWORK_DIAGNOSTICS: flag(
    'NETWORK_DIAGNOSTICS',
    import.meta.env.VITE_FEATURE_NETWORK_DIAGNOSTICS
  ),
  // Development-only network usage meter (Ctrl+Alt+M, window.__netMeter). Never
  // installed in production whatever this says — see services/network-meter.
  NETWORK_METER: flag('NETWORK_METER', import.meta.env.VITE_FEATURE_NETWORK_METER),
  // Device-performance diagnostic page (/debug/device-performance): benchmarks
  // the real pose pipeline for calibration. Never routed in production whatever
  // this says — see services/device-performance.
  DEVICE_PERFORMANCE_DIAGNOSTIC: flag(
    'DEVICE_PERFORMANCE_DIAGNOSTIC',
    import.meta.env.VITE_FEATURE_DEVICE_PERFORMANCE_DIAGNOSTIC
  ),
  // Patient-facing connection status on the exercise screen: banner, voice /
  // video / camera notices and the "What's happening?" panel. Off shows the
  // screen exactly as before it existed.
  CONNECTION_STATUS: flag('CONNECTION_STATUS', import.meta.env.VITE_FEATURE_CONNECTION_STATUS),
  // OpenAI Realtime agent. Falls back to OPENAI_KEY, the switch the pipelines
  // already set (injected at container start), so an unmigrated env keeps working.
  VOICE_AGENT: flag(
    'VOICE_AGENT',
    import.meta.env.VITE_FEATURE_VOICE_AGENT,
    Boolean(ENV.openaiKey)
  ),
});
